<script setup lang="ts">
import { useRouter } from 'vue-router';
import { useUserSectionData } from '../useUserSectionData';
import { useSectionStepsPrefetch } from '../../composables/useSectionStepsPrefetch';
import { computed, inject, watch, onMounted, onUnmounted, nextTick } from 'vue';
import type { ComputedRef } from 'vue';
import StepListItem from 'modules/Step/components/StepListItem/StepListItem.vue';
import ListWrapperCard from 'shared/components/ListWrapperCard/ListWrapperCard.vue';
import TeacherPreviewBanner from 'shared/components/TeacherPreviewBanner/TeacherPreviewBanner.vue';
import type { StepStepType, StepTypeComponnet } from 'src/types/course/step';
import { stepComponentFromStepType } from 'src/modules/Step/useStepData';
import { componentNameKebabCase } from 'shared/composables/strapi/useComponent';
import type { Step } from 'src/modules/Step/models/Step';
import { useGlobalStore } from 'src/stores/global';
import { useEvents } from 'src/shared/composables/useEvents/useEvents';
import { useQuasar } from 'quasar';
import { t } from 'src/boot/i18n';
import type { UserRole } from 'src/types/User';

const router = useRouter();
const globalStore = useGlobalStore();
const { listen } = useEvents();
const $q = useQuasar();
const currentUserRole =
  inject<ComputedRef<UserRole | undefined>>('currentUserRole');
const isSchoolStudent = computed(() => currentUserRole?.value === 'student');
// Teacher preview: free navigation between steps — evaluation must not require
// solving every step. Rows a student would see locked keep a visual hint.
const isTeacher = computed(() => currentUserRole?.value === 'teacher');

interface props {
  userSectionDocId: string;
  userCourseDocId?: string;
}

const props = defineProps<props>();

const currentUserId = inject('currentUserId') as ComputedRef<number>;

if (!props.userSectionDocId || !currentUserId)
  throw new Error('sectionId and currentUserId are required');

const {
  sectionByCurrentId,
  userSectionByCurrentId,
  getSectionStep,
  sectionSteps,
  generateUserStep,
  resetUserSectionProgress,
  nextSectionStep,
} = useUserSectionData({ userSectionDocId: props.userSectionDocId });
const { prefetchStepByDocId } = useSectionStepsPrefetch(sectionSteps);

watch(
  () => userSectionByCurrentId.value,
  async (userSection) => {
    if (!userSection) return;

    const hasStarted = Boolean(
      userSection?.start_date ||
      userSection?.completion_date ||
      (userSection?.time_accumulator || 0) > 0 ||
      (userSection?.score || 0) > 0 ||
      (userSection?.progress?.steps || []).length > 0,
    );

    if (
      isSchoolStudent.value &&
      userSection?.is_assigned === false &&
      hasStarted
    ) {
      $q.notify({
        type: 'warning',
        message: t('SCHOOL_UNASSIGNED_PROGRESS_PAUSED_MESSAGE'),
      });
      await router.replace({ name: 'sections.my.student' });
    }
  },
  { immediate: true },
);

// Initialize global store once on mount
onMounted(() => {
  globalStore.clearHeaderActions();
  globalStore.setTotal(undefined);
  globalStore.setHeaderActions(['action:reset']);

  // Set initial title
  const title = sectionByCurrentId?.value?.section_name || '';
  if (title !== globalStore.title) {
    globalStore.setTitle(title);
  }
});

// Watch for section name changes
watch(
  () => sectionByCurrentId?.value?.section_name,
  (newTitle) => {
    globalStore.setTitle(newTitle || '');
  },
  { immediate: false },
);

// Setup event listener
let unsubscribe: (() => void) | undefined;

onMounted(() => {
  unsubscribe = listen('action:reset', () => {
    // Standalone sections have no userCourseDocId — the reset endpoint only
    // needs the user-section itself, so requiring it here broke standalone
    // reset for everyone (teachers previewing sections hit it immediately).
    if (!currentUserId?.value) throw new Error('currentUserId is required');
    void resetUserSectionProgress();
  });
});

onUnmounted(() => {
  if (unsubscribe) {
    unsubscribe();
  }
});

// Fix the navigation function to prevent reactive loops
async function handleStepListItemClick(stepDocId: string | undefined) {
  try {
    // Extract values to avoid reactive dependencies during navigation
    const userId = currentUserId.value;
    const userCourseDocId = props.userCourseDocId;
    const userSectionDocId = props.userSectionDocId;

    if (!userId) throw new Error('No current user ID');

    prefetchStepByDocId(stepDocId);
    const userStepId = await generateUserStep(stepDocId);
    if (!userStepId) throw new Error('No User Step to run');

    // Use nextTick to ensure DOM updates are complete before navigation
    await nextTick();

    const routeParams = userCourseDocId
      ? {
          name: 'course.step' as const,
          params: {
            userCourseDocId,
            userSectionDocId,
            userStepId: String(userStepId),
            stepDocId: stepDocId || '',
          },
        }
      : {
          name: 'section.steps.step' as const,
          params: {
            userSectionDocId,
            userStepId: String(userStepId),
            stepDocId: stepDocId || '',
          },
        };

    // Use replace instead of push to avoid navigation stack issues
    await router.push(routeParams);
  } catch (error) {
    console.error('Error navigating to step:', error);
    // Handle the error appropriately
  }
}

// The gate a STUDENT sees: only the next step (or already-completed/skipped
// ones) may be opened. Kept as its own function so the teacher affordance can
// show what would be locked while not enforcing it.
function isLockedForStudents(step: Step | null) {
  if (!step) return true;
  const sectionStep = getSectionStep(step.documentId ?? '');
  return (
    nextSectionStep?.value?.id !== step.id &&
    !sectionStep?.completion_date &&
    !sectionStep?.isSkippedFilter
  );
}

function isDisabled(step: Step | null) {
  if (isTeacher.value) return !step;
  return isLockedForStudents(step);
}
</script>

<template>
  <ListWrapperCard
    flat
    class="fit bg-transparent q-pa-none"
  >
    <TeacherPreviewBanner class="q-ma-sm" />
    <q-list separator>
      <StepListItem
        v-for="step in sectionSteps"
        :key="step?.id"
        :disable="isDisabled(step)"
        :locked-hint="
          isTeacher && isLockedForStudents(step)
            ? $t('LOCKED_FOR_STUDENTS')
            : null
        "
        :active="nextSectionStep?.id === step?.id"
        :step-name="step?.step_name"
        :step-type="
          componentNameKebabCase(
            stepComponentFromStepType(
              step?.step_type as StepStepType[],
            ) as StepTypeComponnet,
          ) ?? undefined
        "
        :step-category="step?.step_category"
        :step-score="
          step?.id ? (getSectionStep(step.documentId ?? '')?.score ?? 0) : 0
        "
        :start-date="
          step?.id
            ? getSectionStep(step.documentId ?? '')?.start_date
            : undefined
        "
        :completion-date="
          step?.id
            ? getSectionStep(step.documentId ?? '')?.completion_date
            : undefined
        "
        :is-skipped="
          step?.id
            ? !!getSectionStep(step.documentId ?? '')?.isSkippedFilter
            : false
        "
        @click="handleStepListItemClick(step?.documentId ?? undefined)"
      />
    </q-list>
  </ListWrapperCard>
</template>
